import { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Save, Loader2, Sparkles, EyeOff, Download, Send, Lock, CopyPlus, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * FormFiller — fills a custom form template with values pulled from related
 * CRM records (customer, project, estimate, invoice, job, permit). Renders
 * the original PDF as background and overlays editable inputs at each
 * field's stored position.
 *
 * Props:
 *   - templateId: string (custom form template ID)
 *   - context: { customer_id?, project_id?, estimate_id?, invoice_id?, job_id?, permit_id? }
 *   - linkedType: 'estimate' | 'invoice' | 'job' | 'permit' | 'contact'
 *   - linkedId: id of the parent record this instance is attached to
 *   - instanceId: existing instance id to load (optional)
 *   - onSaved: callback (instance) => void
 */
export default function FormFiller({
  templateId,
  context = {},
  linkedType,
  linkedId,
  instanceId = null,
  onSaved,
}) {
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [resolvedMap, setResolvedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInternal, setShowInternal] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
  const [currentInstanceId, setCurrentInstanceId] = useState(instanceId);
  const [status, setStatus] = useState('empty');
  const containerRef = useRef(null);

  const pdfUrl = template?.pdf_url
    ? `${process.env.REACT_APP_BACKEND_URL}${template.pdf_url}`
    : null;

  // Load template + resolve mapped values + load existing instance if any
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, mapRes] = await Promise.all([
        api.get(`/custom-forms/templates/${templateId}`),
        api.get('/custom-forms/resolve-mapping', { params: context }),
      ]);
      setTemplate(tplRes.data);
      const mapped = mapRes.data?.values || {};
      setResolvedMap(mapped);

      let initial = {};
      if (instanceId) {
        const inst = await api.get(`/custom-forms/instances/${instanceId}`);
        initial = inst.data?.field_values || {};
        setStatus(inst.data?.status || 'draft');
      }

      // Pre-fill from resolved mapping for any field that is still empty
      const fields = tplRes.data?.fields || [];
      const normCheck = (v) => String(v ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '_');
      fields.forEach(f => {
        if (initial[f.id] === undefined) {
          // Checkbox with auto-check binding: tick when the estimate's value matches.
          if (f.type === 'checkbox' && f.auto_check_field) {
            const src = mapped[f.auto_check_field];
            const hasValue = src !== undefined && src !== null && String(src).trim() !== '';
            initial[f.id] = hasValue && normCheck(src) === normCheck(f.auto_check_value);
          } else if (f.mapped_field && mapped[f.mapped_field] !== undefined) {
            initial[f.id] = mapped[f.mapped_field];
          } else if (f.default_value) {
            initial[f.id] = f.default_value;
          }
        }
      });
      setValues(initial);
    } catch (err) {
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [templateId, instanceId, JSON.stringify(context)]);

  useEffect(() => { load(); }, [load]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const onPageLoadSuccess = (page) => {
    setPdfDimensions({ width: page.width, height: page.height });
  };

  const setVal = (fieldId, v) => setValues(prev => ({ ...prev, [fieldId]: v }));

  const handleSave = async (newStatus = 'draft') => {
    setSaving(true);
    try {
      let res;
      if (currentInstanceId) {
        res = await api.put(`/custom-forms/instances/${currentInstanceId}`, {
          field_values: values,
          status: newStatus,
        });
      } else {
        res = await api.post('/custom-forms/instances', {
          template_id: templateId,
          linked_type: linkedType,
          linked_id: linkedId,
          field_values: values,
        });
        setCurrentInstanceId(res.data.id);
      }
      setStatus(res.data.status || newStatus);
      toast.success('Form saved');
      onSaved && onSaved(res.data);
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Save & Send — Alian: "todas estas formas hay que enviarlas por email...
  // le doy Save y Send, y ya esta mandado; se marca como mandado."
  const handleSendAndSave = async () => {
    setSaving(true);
    try {
      let id = currentInstanceId;
      if (!id) {
        const saved = await handleSave('draft');
        if (!saved?.id) return;
        id = saved.id;
      } else {
        await api.put(`/custom-forms/instances/${id}`, { field_values: values });
      }
      const res = await api.post(`/custom-forms/instances/${id}/send`, {});
      setStatus('sent');
      toast.success('Sent by email successfully');
      onSaved && onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the form');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkGranted = async () => {
    if (!currentInstanceId) return;
    setSaving(true);
    try {
      const res = await api.put(`/custom-forms/instances/${currentInstanceId}`, { status: 'granted' });
      setStatus('granted');
      toast.success('Marked as Granted');
      onSaved && onSaved(res.data);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // Requirement F (Alian): "una vez que le haces save/send, esto no debe ser
  // editado. Si querés editar, hacés un PDF nuevo." — instead of unlocking
  // the sent/granted instance, clone it into a fresh draft the user can edit.
  const handleNewVersion = async () => {
    if (!currentInstanceId) return;
    setSaving(true);
    try {
      const res = await api.post(`/custom-forms/instances/${currentInstanceId}/new-version`);
      setCurrentInstanceId(res.data.id);
      setValues(res.data.field_values || {});
      setStatus('draft');
      toast.success('Nueva versión creada — ya puede editar');
      onSaved && onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear una nueva versión');
    } finally {
      setSaving(false);
    }
  };

  // Save (creating instance if needed) and download the filled PDF
  const handleDownloadPdf = async () => {
    let id = currentInstanceId;
    if (!id) {
      // Save first to obtain an instance id
      const saved = await handleSave('draft');
      if (!saved?.id) return;
      id = saved.id;
    } else if (!locked) {
      // Persist current edits before generating PDF (skipped when locked —
      // the backend would reject a field_values write on a sent/granted form)
      try {
        await api.put(`/custom-forms/instances/${id}`, { field_values: values });
      } catch {/* non-blocking */}
    }
    try {
      const res = await api.get(`/custom-forms/instances/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template?.name || 'form'}_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  // Requirement E (Alian, video "Mejoras en proyectos, permisos y
  // reportes"): a one-click way to get this PDF into an external
  // e-recording service (Lien Solutions/Simplifile) without hopping across
  // tabs. Browsers cannot copy a PDF binary that another site can "paste"
  // into a file input — only images/text are paste-able — so we try the
  // best-effort Clipboard API (works in some desktop contexts) and always
  // fall back to an immediate 1-click download so the user is never stuck.
  const handleCopyPdf = async () => {
    let id = currentInstanceId;
    if (!id) {
      const saved = await handleSave('draft');
      if (!saved?.id) return;
      id = saved.id;
    } else if (!locked) {
      try { await api.put(`/custom-forms/instances/${id}`, { field_values: values }); } catch { /* non-blocking */ }
    }
    try {
      const res = await api.get(`/custom-forms/instances/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([new window.ClipboardItem({ 'application/pdf': blob })]);
          toast.success('PDF copiado — pegalo (Ctrl+V) donde subas el archivo');
          return;
        } catch { /* clipboard write not supported for this MIME — fall back below */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template?.name || 'form'}_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.info('Tu navegador no permite copiar el PDF — se descargó automáticamente');
    } catch (err) {
      toast.error('No se pudo copiar/descargar el PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
      </div>
    );
  }
  if (!template) return <div className="p-8 text-center text-gray-500">Template not found</div>;

  const fields = template.fields || [];
  // Requirement F: sent/granted forms are read-only — editing requires a new version.
  const locked = status === 'sent' || status === 'granted';

  // Evaluate conditional display rules at runtime
  const isFieldVisible = (field) => {
    const cond = field.condition;
    if (!cond || !cond.field_id) return true;
    const sourceVal = values[cond.field_id];
    const compareVal = cond.value;
    switch (cond.operator) {
      case 'empty':
        return sourceVal === undefined || sourceVal === null || sourceVal === '' || (Array.isArray(sourceVal) && sourceVal.length === 0);
      case 'not_empty':
        return !(sourceVal === undefined || sourceVal === null || sourceVal === '' || (Array.isArray(sourceVal) && sourceVal.length === 0));
      case 'not_equals':
        return String(sourceVal ?? '') !== String(compareVal ?? '');
      case 'contains':
        return String(sourceVal ?? '').toLowerCase().includes(String(compareVal ?? '').toLowerCase());
      case 'equals':
      default:
        return String(sourceVal ?? '') === String(compareVal ?? '');
    }
  };

  const visibleFields = fields.filter(f => (showInternal ? true : !f.internal_only) && isFieldVisible(f));
  const currentPageFields = visibleFields.filter(f => (f.page || 1) === currentPage);

  const renderFieldInput = (field) => {
    const v = values[field.id] ?? '';
    const isMapped = !!field.mapped_field;
    const disabled = locked;
    const inputStyle = {
      textAlign: field.text_align || 'left',
      fontSize: `${field.content_font_size || 12}px`,
    };
    switch (field.type) {
      case 'cutting_table':
        return (
          <div className="w-full h-full flex items-center justify-center px-2 rounded bg-sky-50/80 border border-dashed border-sky-300 text-[10px] text-sky-700 font-medium">
            Drill Cuttings Log — se rellena automáticamente desde el trabajo
          </div>
        );
      case 'heading':
        return (
          <div className="w-full h-full flex items-center px-1 font-bold uppercase tracking-wide"
               style={{ fontSize: `${field.label_font_size || 16}px`, ...inputStyle }}>
            {field.label}
          </div>
        );
      case 'textarea':
        return (
          <textarea
            value={v}
            onChange={e => setVal(field.id, e.target.value)}
            disabled={disabled}
            className={`w-full h-full px-1.5 py-1 border rounded resize-none ${field.remove_border ? 'border-transparent' : 'border-gray-300'} ${isMapped ? 'bg-emerald-50/60' : 'bg-white'} ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
            style={inputStyle}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={v}
            onChange={e => setVal(field.id, e.target.value)}
            disabled={disabled}
            className={`w-full h-full px-1.5 border rounded ${field.remove_border ? 'border-transparent' : 'border-gray-300'} ${isMapped ? 'bg-emerald-50/60' : 'bg-white'} ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
            style={inputStyle}
          />
        );
      case 'checkbox':
        return (
          <div className="w-full h-full flex items-center justify-center px-1.5">
            <input
              type="checkbox"
              checked={!!v}
              onChange={e => setVal(field.id, e.target.checked)}
              disabled={disabled}
              className="w-4 h-4"
            />
          </div>
        );
      case 'dropdown':
        return (
          <select
            value={v}
            onChange={e => setVal(field.id, e.target.value)}
            disabled={disabled}
            className={`w-full h-full px-1.5 border rounded ${field.remove_border ? 'border-transparent' : 'border-gray-300'} ${isMapped ? 'bg-emerald-50/60' : 'bg-white'} ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
            style={inputStyle}
          >
            <option value="">— Select —</option>
            {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'multiselect': {
        const arr = Array.isArray(v) ? v : (typeof v === 'string' && v ? v.split(',') : []);
        return (
          <div className={`w-full h-full overflow-auto px-1.5 py-1 border rounded ${field.remove_border ? 'border-transparent' : 'border-gray-300'} ${isMapped ? 'bg-emerald-50/60' : 'bg-white'}`}>
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-1 text-xs cursor-pointer" style={inputStyle}>
                <input
                  type="checkbox"
                  checked={arr.includes(opt)}
                  disabled={disabled}
                  onChange={e => {
                    const next = e.target.checked ? [...arr, opt] : arr.filter(o => o !== opt);
                    setVal(field.id, next);
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      }
      case 'quick_select':
        return (
          <div className={`w-full h-full flex items-center gap-2 flex-wrap px-1.5 ${field.remove_border ? '' : 'border rounded border-gray-300'}`}>
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-1 text-xs cursor-pointer" style={inputStyle}>
                <input
                  type="radio"
                  name={field.id}
                  checked={v === opt}
                  disabled={disabled}
                  onChange={() => setVal(field.id, opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case 'image_upload':
        return (
          <div className={`w-full h-full flex items-center justify-center text-xs ${field.remove_border ? '' : 'border-2 border-dashed rounded border-gray-300'} ${isMapped ? 'bg-emerald-50/60' : 'bg-white/80'}`}>
            <input
              type="file"
              accept="image/*,video/*"
              disabled={disabled}
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => setVal(field.id, ev.target?.result);
                reader.readAsDataURL(file);
              }}
              className="text-[10px]"
            />
          </div>
        );
      case 'signature':
        return (
          <div className={`w-full h-full flex items-center justify-center text-xs italic text-gray-400 ${field.remove_border ? '' : 'border-2 border-dashed rounded border-purple-300'} bg-purple-50/40`}>
            {v ? <span className="text-purple-700 font-semibold">✓ Signed</span> : 'Click to sign'}
            {!v && !disabled && (
              <button
                type="button"
                onClick={() => setVal(field.id, `Signed at ${new Date().toLocaleString()}`)}
                className="ml-2 text-[10px] underline text-purple-600"
              >
                Sign
              </button>
            )}
          </div>
        );
      case 'related':
      case 'text':
      case 'number':
      default:
        return (
          <input
            type={field.type === 'number' ? 'number' : 'text'}
            value={v}
            onChange={e => setVal(field.id, e.target.value)}
            disabled={disabled}
            placeholder={field.label}
            className={`w-full h-full px-1.5 border rounded ${field.remove_border ? 'border-transparent' : 'border-gray-300'} ${isMapped ? 'bg-emerald-50/60' : 'bg-white'} ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
            style={inputStyle}
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="form-filler">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">{template.name}</h3>
          {Object.keys(resolvedMap).length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
              <Sparkles className="w-3 h-3" /> {Object.values(resolvedMap).filter(v => v !== '' && v !== null && v !== undefined).length} auto-filled
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
            <Switch
              checked={showInternal}
              onCheckedChange={setShowInternal}
              data-testid="toggle-internal-fields"
            />
            <EyeOff className="w-3 h-3" /> Show internal
          </label>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setScale(s => Math.max(s - 0.1, 0.5))}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs">{Math.round(scale * 100)}%</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setScale(s => Math.min(s + 0.1, 2))}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          {numPages && numPages > 1 && (
            <>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs">{currentPage} / {numPages}</span>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))} disabled={currentPage >= numPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" disabled={saving} onClick={handleDownloadPdf} data-testid="download-filled-pdf-btn" className="border-blue-200 text-blue-700 hover:bg-blue-50">
            <Download className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" disabled={saving} onClick={handleCopyPdf} data-testid="copy-filled-pdf-btn" className="border-purple-200 text-purple-700 hover:bg-purple-50">
            <Copy className="w-4 h-4 mr-1" /> Copiar
          </Button>
          {locked ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 px-2.5 py-1.5 rounded" data-testid="form-filler-locked-badge">
                <Lock className="w-3.5 h-3.5" /> {status === 'granted' ? 'Otorgado — bloqueado' : 'Enviado — bloqueado'}
              </span>
              <Button size="sm" disabled={saving} onClick={handleNewVersion} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="new-version-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CopyPlus className="w-4 h-4 mr-1" />} Crear nueva versión
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" disabled={saving} onClick={() => handleSave('draft')} data-testid="save-draft-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Draft
              </Button>
              <Button size="sm" disabled={saving} onClick={handleSendAndSave} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="save-send-btn">
                <Send className="w-4 h-4 mr-1" /> Save & Send
              </Button>
              {status === 'sent' && (
                <Button size="sm" disabled={saving} onClick={handleMarkGranted} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mark-granted-btn">
                  Mark as Granted
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {locked && (
        <div className="px-3 py-1.5 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-100" data-testid="form-filler-locked-note">
          Esta forma ya fue {status === 'granted' ? 'otorgada' : 'enviada'} y quedó bloqueada para edición. Si necesita corregir algo, cree una nueva versión.
        </div>
      )}

      {/* PDF + overlay */}
      <div className="flex-1 overflow-auto bg-gray-100">
        {pdfUrl ? (
          <div className="flex justify-center p-4" style={{ minHeight: '100%' }}>
            <div ref={containerRef} className="relative bg-white shadow-lg" style={{
              width: pdfDimensions.width * scale || 'auto',
              height: pdfDimensions.height * scale || 'auto',
            }}>
              <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
                <Page pageNumber={currentPage} scale={scale} onLoadSuccess={onPageLoadSuccess}
                      renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
              {currentPageFields.map(field => (
                <div
                  key={field.id}
                  className="absolute"
                  style={{
                    left: `${field.x}%`,
                    top: `${field.y}%`,
                    width: `${field.width}%`,
                    height: `${field.height}%`,
                    minHeight: '20px',
                  }}
                  data-testid={`fill-field-${field.id}`}
                >
                  {renderFieldInput(field)}
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Form-only fallback when no PDF uploaded
          <div className="max-w-2xl mx-auto p-6 space-y-4">
            {currentPageFields.map(field => (
              <div key={field.id} className="space-y-1">
                <Label className="text-sm">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                  {field.internal_only && <EyeOff className="w-3 h-3 inline ml-1 text-amber-500" />}
                </Label>
                <div className="h-10">{renderFieldInput(field)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
